/**
 * projectStore.js — SSOT for NB Corporate Project Data
 * =====================================================
 *
 * This store is the single authoritative source for:
 *   - Project enrichment (CPA grouping, additionality, issuance readiness)
 *   - Portfolio aggregation (totals, pathway split)
 *   - Carbon value computations (VCM + Article 6 scenarios)
 *
 * Both Carbon Structuring and Financials import from here.
 * Both pages read from vbCache via NBPLCContext — the SAME backend dataset.
 * Changing an opportunity's status (transition) invalidates the cache and
 * both pages update automatically from this shared enrichment layer.
 *
 * Architecture:
 *   NBPLCContext.vbCache  → raw opportunity data from backend (SSOT)
 *       ↓
 *   useProjectStore()     → enriched opportunities (CPA, additionality, issuance)
 *       ↓
 *   Carbon Structuring    → uses enriched projects for CPA/PoA display
 *   Financials            → uses enriched projects for portfolio table
 */
import { useMemo } from "react";
import { useNBPLC } from "@/context/NBPLCContext";

// ── CPA Configuration ─────────────────────────────────────────────────────────
// Single definition — shared across ALL pages that use CPA grouping logic

export const CPA_GROUPS = [
  {
    id: "CPA1",
    label: "Energy Systems",
    description: "Renewable energy generation and electricity demand reduction",
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    types: ["solar_pv", "heat_recovery"],
    pathways: ["VCM", "Article 6"],
    methodology: "ACM0002 / AMS.I.A — Renewable energy / efficiency",
  },
  {
    id: "CPA2",
    label: "Fuel Transition",
    description: "Switching from fossil fuels to low-carbon alternatives",
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    types: ["biomass_boiler", "biogas_waste"],
    pathways: ["VCM", "Article 6"],
    methodology: "AMS.I.C / AMS.III.E — Thermal + waste-to-energy",
  },
  {
    id: "CPA3",
    label: "Circularity & Resource",
    description: "Packaging circularity and logistics emission reduction",
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    types: ["returnable_bottles", "logistics_optimization"],
    pathways: ["VCM"],
    methodology: "AMS.III.AJ — Demand-side measures (packaging)",
  },
];

// Flat lookup: opp_type → CPA group
export const CPA_BY_TYPE = {};
CPA_GROUPS.forEach(g => g.types.forEach(t => { CPA_BY_TYPE[t] = g; }));


// ── Enrichment Functions (SSOT) ────────────────────────────────────────────────

/**
 * Returns CPA group for an opportunity.
 * Returns null for internal-pathway projects.
 */
export function getCPAGroup(opp) {
  return CPA_BY_TYPE[opp.opp_type] || null;
}

/**
 * Additionality Signal (Preliminary) — structured labeling.
 * Based on IRR tier and intervention type.
 * Used in: Structuring, Financials
 */
export function getAdditionalitySignal(opp) {
  const irr = opp.irr_pct || 0;
  if (irr > 22)                                    return { label: "Common practice test", sub: "High IRR — formal additionality proof required",    cls: "text-red-600" };
  if (opp.opp_type === "logistics_optimization")   return { label: "Regulatory requirement",  sub: "Operational compliance driver",                   cls: "text-amber-600" };
  return                                                  { label: "Investment barrier",       sub: "Capital cost limits unaided viability",            cls: "text-emerald-700" };
}

/**
 * Issuance Readiness tag — based on pipeline status.
 * Used in: Structuring (eligibility table)
 */
export function getIssuanceReadiness(status) {
  if (["financing_ready", "executed"].includes(status))
    return { label: "Issuance Ready",    cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (["structuring_in_progress","structuring_ready","financing_review"].includes(status))
    return { label: "Structuring Phase", cls: "bg-amber-100 text-amber-700 border-amber-200" };
  return   { label: "Early Stage",       cls: "bg-slate-100 text-slate-500 border-slate-200" };
}

/**
 * Strength label — based on IRR.
 * Used in: Financials
 */
export function getStrength(irr) {
  if (irr > 20) return { label: "Strong",   cls: "text-emerald-700 font-bold" };
  if (irr >= 12) return { label: "Moderate", cls: "text-amber-600 font-bold" };
  return               { label: "Weak",     cls: "text-red-600 font-bold" };
}

/**
 * Pathway label — derived from eligibility flags.
 */
export function getPathwayLabel(opp) {
  if (opp.art6_eligible && opp.vcm_eligible) return "VCM + Art.6";
  if (opp.vcm_eligible)                       return "VCM";
  return "Internal";
}


// ── Main Store Hook ────────────────────────────────────────────────────────────

/**
 * useProjectStore — enriched project data for the NB workspace.
 *
 * Wraps useNBPLC and adds computed fields to every opportunity.
 * Downstream pages (Structuring, Financials) call this ONE hook.
 *
 * Returns everything from useNBPLC PLUS:
 *   projects         — enriched opportunities array (with cpa_group, additionality, etc.)
 *   vbData           — raw VB response for the selected period
 *   portfolio        — portfolio aggregation object
 *   vcmProjects      — projects eligible for VCM
 *   art6Projects     — projects eligible for Article 6
 *   poaProjects      — all PoA-eligible projects (vcm OR art6)
 *   inStructuring    — projects currently in structuring stage
 */
/**
 * useOkoObaStructuringData — adapts Oko-Oba single-project journey data
 * for the 4-section structuring architecture.
 *
 * Uses the SAME enrichment functions as useProjectStore (getCPAGroup, etc.)
 * so both workspaces share consistent logic — zero duplication.
 *
 * Called from GenericCarbonStructuring.js with data from useProjectWorkspace().
 * Does NOT call useNBPLC or any NB-specific hook — zero cross-workspace leakage.
 *
 * @param {object} journey    — full journey object from useProjectWorkspace
 * @param {object} reduction  — { total_tco2e_yr, methane_avoidance_tco2e_yr, ... }
 * @param {object} carbonPath — carbon_pathway object from backend
 */
export function useOkoObaStructuringData({ journey = {}, reduction = {}, carbonPath = {} } = {}) {
  const totalRed   = Number(reduction?.total_tco2e_yr || 0);
  const capex      = Number(journey?.capex_usd || 0);
  const savings    = Number(journey?.opex_savings_usd_yr || 0);
  const defaultIrr = 16;  // Conservative default — investment barrier criterion

  // Build virtual "activity" objects from real project data
  const rawActivities = useMemo(() => {
    if (!totalRed) return [];
    return [{
      opp_id:              "OKO-ACT-01",
      name:                "Methane Avoidance & Biogas Recovery",
      opp_type:            "biogas_waste",
      reduction_tco2e_yr:  totalRed,
      capex_usd:           capex,
      cost_savings_usd_yr: savings,
      circularity_value_usd_yr: 0,
      guaranteed_value_usd_yr: savings,
      carbon_rev_base_usd_yr:  Math.round(totalRed * 25),
      irr_pct:             defaultIrr,
      payback_years:       capex > 0 && savings > 0 ? parseFloat((capex / savings).toFixed(1)) : 8,
      vcm_eligible:        (carbonPath?.pathways?.vcm?.status === "recommended"),
      art6_eligible:       (carbonPath?.pathways?.article6?.status === "recommended"),
      pathway:             "vcm",
      composite_score:     72,
      status:              "structuring_in_progress",
      sdg_tags:            [6, 7, 13],
    }];
  }, [totalRed, capex, savings, carbonPath]);

  // Enrich with shared store functions — same as NB
  const activities = useMemo(() => rawActivities.map(a => ({
    ...a,
    cpa_group:          getCPAGroup(a),
    additionality:      getAdditionalitySignal(a),
    issuance_readiness: getIssuanceReadiness(a.status),
    strength:           getStrength(a.irr_pct || 0),
    pathway_label:      getPathwayLabel(a),
  })), [rawActivities]);

  // CPA groups derived from activities
  const cpaGroups = useMemo(() =>
    CPA_GROUPS
      .map(g => ({
        ...g,
        projects:    activities.filter(a => g.types.includes(a.opp_type)),
        poaProjects: activities.filter(a => g.types.includes(a.opp_type) && (a.vcm_eligible || a.art6_eligible)),
        totalRed:    activities.filter(a => g.types.includes(a.opp_type)).reduce((s,a)=>s+(a.reduction_tco2e_yr||0), 0),
      }))
      .filter(g => g.projects.length > 0),
  [activities]);

  // Conditional section visibility
  const showCPA = cpaGroups.length > 1;
  const poaStatus = carbonPath?.pathways?.poa?.status;
  const showPoA   = showCPA || poaStatus === "recommended";

  // Portfolio / aggregation (renamed to avoid confusion with NB)
  const aggregation = useMemo(() => {
    const vcmRed  = activities.filter(a=>a.vcm_eligible).reduce((s,a)=>s+(a.reduction_tco2e_yr||0),0);
    const art6Red = activities.filter(a=>a.art6_eligible).reduce((s,a)=>s+(a.reduction_tco2e_yr||0),0);
    const blended = savings > 0 && capex > 0 ? `${(capex/savings).toFixed(1)}yr` : "—";
    return { totalRed, totalCap: capex, totalSav: savings, opValue: savings, vcmRed, art6Red, blended };
  }, [activities, totalRed, capex, savings]);

  return { activities, cpaGroups, showCPA, showPoA, aggregation, poaStatus };
}


// ── Main Store Hook (NB Corporate) ────────────────────────────────────────────
export function useProjectStore() {
  const nb = useNBPLC();
  const vbData = nb.selectedPeriod ? (nb.vbCache[nb.selectedPeriod] || null) : null;
  const rawOpps = vbData?.opportunities || [];

  // Enrich all opportunities with computed fields — single pass
  const projects = useMemo(() => rawOpps.map(o => ({
    ...o,
    cpa_group:         getCPAGroup(o),
    additionality:     getAdditionalitySignal(o),
    issuance_readiness: getIssuanceReadiness(o.status),
    strength:          getStrength(o.irr_pct || 0),
    pathway_label:     getPathwayLabel(o),
  })), [rawOpps]);

  // Derived subsets
  const vcmProjects     = useMemo(() => projects.filter(o => o.vcm_eligible),          [projects]);
  const art6Projects    = useMemo(() => projects.filter(o => o.art6_eligible),          [projects]);
  const poaProjects     = useMemo(() => projects.filter(o => o.vcm_eligible || o.art6_eligible), [projects]);
  const inStructuring   = useMemo(() => projects.filter(o => ["structuring_in_progress","structuring_ready"].includes(o.status)), [projects]);

  // Portfolio aggregation (SSOT for all totals)
  const portfolio = useMemo(() => {
    const totalRed  = projects.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);
    const totalCap  = projects.reduce((s,o) => s + (o.capex_usd||0), 0);
    const totalSav  = projects.reduce((s,o) => s + (o.cost_savings_usd_yr||0), 0);
    const totalCirc = projects.reduce((s,o) => s + (o.circularity_value_usd_yr||0), 0);
    const vcmRed    = vcmProjects.reduce((s,o)  => s + (o.reduction_tco2e_yr||0), 0);
    const art6Red   = art6Projects.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);
    const poaRed    = poaProjects.reduce((s,o)  => s + (o.reduction_tco2e_yr||0), 0);
    const blended   = totalSav > 0 ? (totalCap / totalSav).toFixed(1) + "yr" : "—";
    return {
      totalRed, totalCap, totalSav, totalCirc, vcmRed, art6Red, poaRed, blended,
      opValue: totalSav + totalCirc,
      coverage: vbData?.portfolio?.baseline_coverage_pct || 0,
      baselineAnnual: vbData?.portfolio?.baseline_annual_tco2e || 0,
    };
  }, [projects, vcmProjects, art6Projects, poaProjects, vbData]);

  // CPA groups with enriched data
  const cpaGroups = useMemo(() => CPA_GROUPS.map(g => ({
    ...g,
    projects:    projects.filter(o => g.types.includes(o.opp_type)),
    poaProjects: poaProjects.filter(o => g.types.includes(o.opp_type)),
    totalRed:    projects.filter(o => g.types.includes(o.opp_type)).reduce((s,o)=>s+(o.reduction_tco2e_yr||0),0),
  })), [projects, poaProjects]);

  return {
    // Re-export everything from NBPLCContext
    ...nb,
    // Store-level enriched data
    projects,
    vbData,
    portfolio,
    vcmProjects,
    art6Projects,
    poaProjects,
    inStructuring,
    cpaGroups,
    // Helpers (exported for use in JSX without re-import)
    getCPAGroup,
    getAdditionalitySignal,
    getIssuanceReadiness,
    getStrength,
    getPathwayLabel,
  };
}
